import { computed, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import {
  liveLineModel,
  liveRowLines,
  liveRowView,
} from '@/domain/menu-import/live-rows';
import type {
  ILiveLineModel,
  ILiveRowLines,
  ILiveRowView,
} from '@/domain/menu-import/live-rows';
import { liveItemState } from '@/domain/menu-import/live-update';
import type { TLiveItemState } from '@/domain/menu-import/live-update';
import type { IDraftItem, ILiveOther } from '@/interfaces/IMenuImport';

export interface ILiveRowOptions {
  /** The dish the row shows. */
  item: () => IDraftItem;
  /** The dish as it is now (the editor edits it, never a proposed version). */
  editorItem: () => IDraftItem;
  /** Update sessions: the menu's dishes no dish is linked to; else null. */
  liveOthers: () => ILiveOther[] | null;
  /** Review lines show (not while reading, no ghost, no proposal mark). */
  reviewShown: () => boolean;
  /** The row's editor is open. */
  open: () => boolean;
  /** «$ 9.800»; «—» for null. */
  money: (value: number | null) => string;
  tr: (key: string, params?: Record<string, unknown>) => string;
}

/** A size badge of a dish already on the menu, ready to render. */
export interface ILiveRowSizeBadge {
  key: string;
  label: string;
  /** Today's price, struck (only when it changes). */
  was: string | null;
  now: string;
  /** A size only the photo has («Nuevo»). */
  isNew: boolean;
  /** «Antes {from}, ahora {to}» for screen readers, when it changes. */
  aria: string | null;
}

export interface IUseLiveRow {
  /** The update look: a matched draft, reviewed, without a proposal mark. */
  updateRow: ComputedRef<boolean>;
  liveState: ComputedRef<TLiveItemState | null>;
  /** A dish already on the menu: its name and prices are the menu's. */
  liveView: ComputedRef<ILiveRowView | null>;
  liveLines: ComputedRef<ILiveRowLines | null>;
  liveLine: ComputedRef<ILiveLineModel | null>;
  /** «Nuevo» next to the name (a new or suggested dish). */
  isNewDish: ComputedRef<boolean>;
  liveSizeBadges: ComputedRef<ILiveRowSizeBadge[]>;
  /** The menu's name of a dish already on it, else the dish's. */
  displayName: ComputedRef<string>;
  /** The element around the open editor (the focus check of the switch). */
  editorBox: Ref<HTMLElement | null>;
  /** The open editor is the price-only one of a dish already on the menu. */
  liveEditor: Ref<boolean>;
  /** One editor per linked dish: a relink shows the new dish's editor. */
  editorKey: Ref<string>;
  /** Picks the editor for the link as it is now (on open, and after the owner's own relink). */
  pickEditor(): void;
  /** The focus left the editor: a switch waiting for it happens now. */
  onEditorFocusOut(event: FocusEvent): void;
}

/**
 * A dish row of an update session (plan §3.5 «Dish rows»): what it shows
 * (the menu's name and prices, the sub-lines, the live line) and which
 * editor it opens. The editor is decided when it opens; a link made or
 * removed elsewhere switches it only once the focus leaves it (never under
 * the owner's typing: the phone keyboard would close). Call it in the row's
 * setup.
 */
export function useLiveRow(options: ILiveRowOptions): IUseLiveRow {
  const updateRow = computed(
    () => options.liveOthers() !== null && options.reviewShown()
  );
  const liveState = computed(() =>
    updateRow.value ? liveItemState(options.item()) : null
  );
  const liveView = computed(() =>
    updateRow.value ? liveRowView(options.item()) : null
  );
  const liveLines = computed(() =>
    updateRow.value ? liveRowLines(options.item()) : null
  );
  const liveLine = computed(() => {
    const others = options.liveOthers();
    return updateRow.value && others !== null
      ? liveLineModel(options.item(), others, { money: options.money })
      : null;
  });
  const isNewDish = computed(
    () => liveState.value === 'new' || liveState.value === 'suggested'
  );
  const liveSizeBadges = computed(() =>
    (liveView.value?.sizes ?? []).map(
      (size): ILiveRowSizeBadge => {
        const changed = size.today !== null && size.next !== null;
        return {
          key: size.key,
          label: size.label,
          was: changed ? options.money(size.today) : null,
          now: options.money(size.next ?? size.today),
          isNew: size.today === null,
          aria: changed
            ? options.tr('menuImport.update.priceAria', {
                from: options.money(size.today),
                to: options.money(size.next),
              })
            : null,
        };
      }
    )
  );
  const displayName = computed(
    () => liveView.value?.name ?? options.item().name
  );

  //#region The editor it opens
  const editorBox = ref<HTMLElement | null>(null);
  /** The dish of the menu this one updates, else null. */
  const linkedDishId = computed(
    () => options.editorItem().live?.dish?.itemId ?? null
  );
  const liveEditor = ref(false);
  const editorKey = ref('plain');
  /** The link changed while the owner types in the editor: it switches once they leave it. */
  let editorSwitchPending = false;

  function pickEditor() {
    editorSwitchPending = false;
    liveEditor.value =
      linkedDishId.value !== null && options.liveOthers() !== null;
    editorKey.value = liveEditor.value ? `live-${linkedDishId.value}` : 'plain';
  }

  watch(
    options.open,
    (open) => {
      if (open) pickEditor();
    },
    { immediate: true }
  );

  // Linked or unlinked elsewhere (another device, a new match): never swap
  // the editor holding the focus (it would close the phone keyboard).
  watch(linkedDishId, () => {
    if (!options.open()) return;
    const active = document.activeElement;
    if (active instanceof Node && editorBox.value?.contains(active))
      editorSwitchPending = true;
    else pickEditor();
  });

  function onEditorFocusOut(event: FocusEvent) {
    if (!editorSwitchPending) return;
    const next = event.relatedTarget;
    if (next instanceof Node && editorBox.value?.contains(next)) return;
    pickEditor();
  }
  //#endregion

  return {
    updateRow,
    liveState,
    liveView,
    liveLines,
    liveLine,
    isNewDish,
    liveSizeBadges,
    displayName,
    editorBox,
    liveEditor,
    editorKey,
    pickEditor,
    onEditorFocusOut,
  };
}
